import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth, type User } from "../auth/AuthContext";
import { useAuthedFetch } from "../auth/apiClient";

export type AutomationStatus = "ok" | "failed" | "paused" | "inactive" | "active" | "unknown";

export interface Automation {
  unit: string;
  label?: string;
  status: AutomationStatus;
  lastRun: string | null;
  nextRun: string | null;
  result: string | null;
  activeState: string | null;
  timerActiveState: string | null;
  enabled: boolean;
  error?: string;
}

export interface AutomationActionResult {
  ok: boolean;
  action: "pause" | "resume" | "trigger";
  target: string;
  audit_id: number;
  journal_snippet?: string;
  message?: string;
}

export interface AutomationHistoryRow {
  id: number;
  ts_utc: string;
  actor: string;
  action: string;
  target: string;
  status: string;
  finished_at: string | null;
  journal_snippet: string | null;
  error: string | null;
}

function actorFromUser(user: User | null) {
  return user?.email || user?.username || "dashboard-owner";
}

async function parseJsonResponse<T>(res: Response, fallback: string): Promise<T> {
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      body && typeof body === "object" && "message" in body
        ? String((body as { message: unknown }).message)
        : body && typeof body === "object" && "error" in body
          ? String((body as { error: unknown }).error)
          : fallback;
    throw new Error(message);
  }
  return body as T;
}

export function useAutomations() {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: ["cockpit", "automations"],
    queryFn: async () => {
      const res = await authedFetch("/api/cockpit/automations", {
        headers: { Accept: "application/json" },
      });
      return parseJsonResponse<{ generatedAt: string; automations: Automation[] }>(
        res,
        "Automations status failed",
      );
    },
    refetchInterval: 30_000,
  });
}

export function useAutomationHistory(limit = 20) {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: ["cockpit", "automation-history", limit],
    queryFn: async () => {
      const res = await authedFetch(`/api/cockpit/history?limit=${limit}`, {
        headers: { Accept: "application/json" },
      });
      return parseJsonResponse<{ generatedAt: string; actions: AutomationHistoryRow[] }>(
        res,
        "Automation history failed",
      );
    },
  });
}

export function useAutomationActions() {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  const { user } = useAuth();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["cockpit", "automations"] });
    void qc.invalidateQueries({ queryKey: ["cockpit", "automation-history"] });
  };

  const runAction = async ({
    action,
    unit,
  }: {
    action: "pause" | "resume" | "trigger";
    unit: string;
  }) => {
    const res = await authedFetch(`/api/cockpit/actions/${action}/${encodeURIComponent(unit)}`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-Actor": actorFromUser(user),
      },
    });
    return parseJsonResponse<AutomationActionResult>(res, `${action} failed`);
  };

  return {
    pause: useMutation({
      mutationFn: (unit: string) => runAction({ action: "pause", unit }),
      onSuccess: invalidate,
    }),
    resume: useMutation({
      mutationFn: (unit: string) => runAction({ action: "resume", unit }),
      onSuccess: invalidate,
    }),
    trigger: useMutation({
      mutationFn: (unit: string) => runAction({ action: "trigger", unit }),
      onSuccess: invalidate,
    }),
  };
}
